import { useRouter } from 'next/router';
import { toast } from 'react-toastify';
import type { GetStaticPaths, GetStaticProps } from 'next';
import FeedForm, { IFormData } from '@components/pages/tools/feeds/FeedForm';
import useI18n, { getLocalePaths } from '@i18n';
import routes from '@config/routes.json';
import api from '@api/index';
import BaseToast from '@base/BaseToast';

// HOCs
import WithRole, { rolesEnum } from '@HOCs/WithRole';

const CreateFeed: React.FC = () => {
    const { t, locale } = useI18n('Feeds');
    const router = useRouter();

    const onSubmit = async (data: IFormData) => {
        const {
            feedName,
            subid: sub,
            domain,
            placement,
            offer: { id: offerId },
            dump,
            category: categories,
            currency,
            format,
            deeplink,
        } = data;

        try {
            await api.feeds.createFeed({
                body: {
                    feedName,
                    sub,
                    domain: domain ? domain.domain ?? domain : '',
                    placementId: placement,
                    offerId,
                    offerDumpId: dump?.value || dump?.id,
                    categories,
                    currency: currency.value ?? currency,
                    format: format.value ?? format,
                    deepLinkHash: deeplink?.hash ?? deeplink,
                },
            });

            await router.push(`/${locale}${routes.tools.feeds}`);

            toast.success(<BaseToast text={t('Feed was successfully created')} type="success" />, {
                toastId: 'feed_creation',
            });
        } catch (err) {
            toast.error(<BaseToast text={t('Feed was not created')} type="error" />, {
                toastId: 'feed_creation',
            });
        }
    };

    return (
        <div>
            <h4 className="font-medium text-blueGray-600 text-h2">{t('New product feed')}</h4>

            <FeedForm linkBack={`/${locale}${routes.tools.feeds}`} onSubmit={onSubmit} />
        </div>
    );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    return { props: { locale: params?.locale } };
};

export const getStaticPaths: GetStaticPaths = async () => {
    return { paths: getLocalePaths(), fallback: false };
};

export default WithRole(CreateFeed, [rolesEnum.user]);
